/**
 * Leads board row — the pure formatters (LF-5986 / LEADS-19).
 *
 * The row's *label maps* live in `./lead-labels` beside LEADS-18's, so the row,
 * the board chrome and the drawer share one definition of each enum's copy.
 *
 * **Formatting only. No state.** `technical-spec.md` §12.4 and the board row
 * DTO's own docblock draw the line: every *derived fact* (section, stall,
 * effective stage, cost-agreement standing, ordering) arrives settled from the
 * server and the frontend re-derives none of it. What does live here is
 * **rendering** — the row's title rule, the compressed meeting string, the
 * short age, the human labels for the DTO's enum values. Those are the
 * frontend's own per `functional-spec.md` §3.5, which is why the DTO ships raw
 * inputs for them rather than pre-rendered strings.
 *
 * The one clock-dependent call, `formatMeetingCell`, takes `now` as an argument
 * so a test pins it rather than racing midnight.
 */

import type { LeadBoardRow, LeadBoardUser } from '@/lib/services/leads';
import { LEAD_SOURCE_LABELS } from './lead-labels';

// =============================================================================
// Formatters
// =============================================================================

/**
 * A firm user's display name — `TeamAvatars` takes names, not records.
 *
 * **Falls back to the email, because both name columns are nullable.** A firm
 * user who has been invited and has not accepted yet has neither name, and the
 * template literal this used to be rendered the literal string `null null` on
 * the row and initialised it to `NN` in the avatar (LF-6015). `.trim()` and
 * `|| fallback` are both no help — `'null null'` is a non-empty, truthy string —
 * so the nulls have to be filtered out before they are joined.
 *
 * The email is the firm-wide treatment for a nameless user, not a local
 * invention: `getUserDisplayName` does the same, and the matter list's Team
 * Members filter already shows it (pinned by
 * `e2e/lf4905-team-members-filter-name-only.spec.ts`).
 */
export function userDisplayName(user: LeadBoardUser): string {
  const parts = [user.firstName, user.lastName].filter(Boolean);
  return parts.length > 0 ? parts.join(' ') : user.email;
}

/**
 * §3.5.12 — `today` / `Nd` under a fortnight / `Nw` above, the market's
 * time-in-stage idiom. Sized to the `w-10` Age column.
 */
export function formatDays(days: number): string {
  if (days <= 0) return 'today';
  if (days < 14) return `${days}d`;
  return `${Math.round(days / 7)}w`;
}

/**
 * Written out rather than taken from `date-fns` or `toLocaleDateString`.
 *
 * Two reasons, both practical: these formats are bespoke (`5/8`, `2.30pm`,
 * `26 July 2026`), so a date library buys nothing here; and the repo ships a
 * **global manual mock** at `frontend/__mocks__/date-fns.js` that jest applies
 * to every suite automatically, whose `format` returns the constant
 * `'Dec 31, 2025'` and which has no `isSameDay` at all. A component formatting
 * through `date-fns` is therefore not unit-testable here without an `unmock`
 * incantation every future test author would have to remember — and if one
 * forgot, the row would silently render a fixed date rather than fail.
 */
const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December'
] as const;

/** `5/8` — day and month, the way it is written on a form. */
function formatNumericDayMonth(at: Date): string {
  return `${at.getDate()}/${at.getMonth() + 1}`;
}

/** Same calendar day in the viewer's own timezone. */
function isSameCalendarDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/**
 * The time half of the meeting cell — `10am`, `2.30pm`.
 *
 * `:00` drops (an on-the-hour meeting does not need it), the separator is a
 * **full stop** rather than a colon, and the meridiem is lower case. All three
 * exist to fit `Today 11.30am` — the worst case at 98px — inside the 104px
 * Meeting column (§3.4).
 */
function formatMeetingTime(at: Date): string {
  const hour = at.getHours() % 12 || 12;
  const minutes = `${at.getMinutes()}`.padStart(2, '0');
  const meridiem = at.getHours() < 12 ? 'am' : 'pm';
  return minutes === '00'
    ? `${hour}${meridiem}`
    : `${hour}.${minutes}${meridiem}`;
}

/**
 * §3.5.11 — the Meeting cell's compressed string.
 *
 * Upcoming keeps **date and time** (you have to be somewhere): today's date
 * becomes the word `Today`, otherwise a numeric `d/m` with the year dropped.
 * Once the meeting is past, the hour answers nothing and only the date
 * survives, prefixed `Met`.
 *
 * **`isPast` is the server's `nextMeetingIsPast`, never re-derived here** — a
 * board read derives every row against one shared instant, and comparing
 * against the browser's clock instead would let a row disagree with the
 * ordering it was placed in.
 *
 * A numeric date is deliberate and confined to this cell: it is the one place a
 * date is *scanned* rather than read, and spelling the month cost ~16px of a
 * column whose width comes straight off the identity column. Every place a
 * date is read — the tooltip, the drawer, the timeline — keeps the spelled
 * month.
 */
export function formatMeetingCell(
  isoAt: string,
  isPast: boolean,
  now: Date = new Date()
): string {
  const at = new Date(isoAt);
  if (isPast) return `Met ${formatNumericDayMonth(at)}`;
  const datePart = isSameCalendarDay(at, now)
    ? 'Today'
    : formatNumericDayMonth(at);
  return `${datePart} ${formatMeetingTime(at)}`;
}

/** The spelled form, for tooltips and anywhere a date is read not scanned. */
export function formatSpelledDate(isoAt: string): string {
  const at = new Date(isoAt);
  return `${at.getDate()} ${MONTH_NAMES[at.getMonth()]} ${at.getFullYear()}`;
}

/** The spelled day and month, no year — the Activity timeline's date column. */
export function formatSpelledDayMonth(isoAt: string): string {
  const at = new Date(isoAt);
  return `${at.getDate()} ${MONTH_NAMES[at.getMonth()].slice(0, 3)}`;
}

/**
 * §4.7's `{ago}` — the elapsed phrase the Status narrative drops mid-sentence
 * (`Enquiry captured 3 days ago, no intake form sent.`).
 *
 * **Day-granular, and deliberately not `formatRelativeTime`.** That shipped util
 * is minute-and-hour precise and switches to an absolute date past seven days,
 * both of which are wrong inside this prose: nobody describes an enquiry as "17
 * minutes ago", and *"The cost agreement went out Jul 26, 2026 and has not come
 * back"* changes tense mid-clause. A lead's narrative measures in days and
 * weeks, so this rounds to those and keeps reading as a sentence.
 *
 * `now` is a parameter for the same reason `formatMeetingCell` takes one: a unit
 * test pins it rather than racing midnight.
 *
 * Elapsed days, not calendar days — matching `daysInStage`, which is elapsed
 * precisely so the same lead does not read `3d` in Perth and `4d` in Sydney.
 */
export function formatLeadAgo(isoAt: string, now: Date = new Date()): string {
  const elapsedMs = now.getTime() - new Date(isoAt).getTime();
  const days = Math.floor(elapsedMs / (24 * 60 * 60 * 1000));

  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 14) return `${days} days ago`;

  const weeks = Math.round(days / 7);
  if (weeks < 9) return `${weeks} weeks ago`;

  const months = Math.round(days / 30);
  return months === 1 ? 'a month ago' : `${months} months ago`;
}

/** The spelled date plus the time — the Meeting cell's tooltip. */
export function formatSpelledDateTime(isoAt: string): string {
  return `${formatSpelledDate(isoAt)} at ${formatMeetingTime(new Date(isoAt))}`;
}

/**
 * §3.5.14 — `$1.2m` / `$840k` at a glance, from the DTO's **cents**.
 *
 * Cents, not dollars: `net_asset_value_cents` is BIGINT cents precisely so
 * money never rides on a float, and the conversion belongs at the render edge
 * rather than in a shared type.
 */
export function formatEstateValue(cents: number | null): string | null {
  if (cents == null) return null;
  const dollars = cents / 100;
  if (dollars >= 1_000_000) {
    const millions = dollars / 1_000_000;
    return `$${millions >= 10 ? millions.toFixed(0) : millions.toFixed(1)}m`;
  }
  if (dollars >= 1_000) return `$${Math.round(dollars / 1_000)}k`;
  return `$${Math.round(dollars)}`;
}

/**
 * §3.5.14 — the grey secondary note, as one truncating block:
 * `{shared notes OR source · referredBy} · est. {value}`.
 *
 * **The lead's shared notes take this slot whenever there are any.** What a
 * colleague typed about the enquiry is what somebody scanning the board actually
 * wants — where the enquiry came from is background, and it was occupying the
 * one line of prose the row has. So the note wins, and the source and referrer
 * fall back to it only on a lead nobody has written anything about (which is a
 * real and common state, so the fallback matters: dropping them outright would
 * leave those rows emptier rather than simpler).
 *
 * Estate value survives either way. It is a different kind of fact from prose —
 * a triage number you scan for, not a sentence — and it lives here rather than
 * in a column because it is context about the lead.
 *
 * **Notes are whitespace-collapsed, not truncated here.** The notes box is a
 * textarea, so the stored text carries newlines and runs of spaces; the row is
 * one line, and HTML would collapse them anyway, but doing it explicitly is what
 * keeps the `title` tooltip and any future measurement honest. Where to cut the
 * text is the renderer's job (`min-w-0` + `truncate`), not this function's.
 *
 * Absent parts drop out rather than rendering an empty separator.
 */
export function secondaryNoteParts(
  row: Pick<
    LeadBoardRow,
    'businessSource' | 'referredBy' | 'netAssetValueCents' | 'moreInfo'
  >
): string[] {
  const value = formatEstateValue(row.netAssetValueCents);
  const notes = row.moreInfo?.replace(/\s+/g, ' ').trim();

  const lead = notes
    ? [notes]
    : [
        row.businessSource ? LEAD_SOURCE_LABELS[row.businessSource] : null,
        row.referredBy
      ];

  return [...lead, value ? `est. ${value}` : null].filter(
    (part): part is string => !!part
  );
}
